import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth } from '../firebase'
import fetchWithAuth from '../utils/fetchWithAuth'
import { useToast } from '../context/ToastContext'
import { Capacitor } from '@capacitor/core'

// Only import types for BackendUser; no runtime revenuecat import on web
import type { Purchases as PurchasesType } from '@revenuecat/purchases-capacitor'

const isNative = Capacitor.getPlatform() !== 'web'
let Purchases: typeof PurchasesType
if (isNative) {
  // @ts-ignore
  Purchases = require('@revenuecat/purchases-capacitor').Purchases
}

type BackendUser = {
  id: string
  email: string
  username: string
  name: string
  avatarUrl: string | null
  isEmailVerified: boolean
  onboardingCompleted: boolean
}

interface AuthContextValue {
  firebaseUser: any | null
  backendUser: BackendUser | null
  refreshBackendUser: () => Promise<void>
  loading: boolean
  setBackendUser: (u: BackendUser | null) => void
  user: any | null
  deleteAccount: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)
export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<any | null>(undefined)
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null)
  const [loading, setLoading] = useState(true)
  const { showToast } = useToast() as any
  const [rcInitialized, setRcInitialized] = useState(false)

  const refreshBackendUser = useCallback(async () => {
    try {
      const profile = await fetchWithAuth('/api/users/me')
      setBackendUser(profile)
    } catch (err) {
      console.error('Failed to refresh backend user:', err)
    }
  }, [])

  useEffect(() => {
    let listenerToken: string | undefined
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser)
      if (fbUser) {
        // fetch backend profile
        try {
          const profile = await fetchWithAuth(`/api/users/${fbUser.uid}`)
          setBackendUser(profile)
        } catch {
          setBackendUser(null)
        }

        // only on native: configure RC with your Firebase UID, link, then listen
        if (isNative && !rcInitialized) {
          try {
            // 1️⃣ Configure RC so it knows who you are
            await Purchases.configure({
              apiKey: process.env.REACT_APP_REVENUECAT_API_KEY_IOS!,
              appUserID: fbUser.uid,
            })

            // 2️⃣ Immediately link that RC ID to your backend
            await fetchWithAuth('/api/subscription/link-revenuecat', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ rcAppUserId: fbUser.uid }),
            })

            // 3️⃣ Subscribe to any customer‐info updates
            listenerToken = await Purchases.addCustomerInfoUpdateListener(() => {
              refreshBackendUser().catch(console.error)
            })

            console.log('✅ RevenueCat configured, linked & listener attached')
            setRcInitialized(true)
          } catch (err) {
            console.error('RevenueCat init error', err)
          }
        }
      } else {
        setBackendUser(null)
      }
      setLoading(false)
    })
    return () => {
      // stop listening to Firebase
      unsub()
      // if we registered a RevenueCat listener, remove it
      if (listenerToken) {
        Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: listenerToken })
      }
    }
  }, [refreshBackendUser])

  const deleteAccount = async () => {
    if (!firebaseUser) throw new Error('Not signed in')
    await fetchWithAuth(`/api/users/${firebaseUser.uid}`, { method: 'DELETE' })
    await signOut(auth)
    setFirebaseUser(null)
    setBackendUser(null)
    showToast({ message: 'Your account has been deleted.', type: 'success' })
  }

  return (
    <AuthContext.Provider
      value={{
        user: firebaseUser,
        firebaseUser,
        backendUser,
        refreshBackendUser,
        loading,
        setBackendUser,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}